import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Button,
	Box,
	Typography,
	Alert,
	CircularProgress,
	Input,
	ListItemIcon,
	ListItemText,
	Chip,
	Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import LabelIcon from "@mui/icons-material/Label";
import { getApiBaseUrl, getEnvironment } from "../../utils/apiConfig";
import { apiClient } from "../../services/api";

const StyledDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		borderRadius: "3px",
		maxWidth: "760px",
		width: "100%",
		margin: "16px",
		boxShadow:
			"0 20px 32px -8px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
		border: "none",
	},
}));

const FileUploadBox = styled(Box)(({ theme }) => ({
	border: "2px dashed #C1C7D0",
	borderRadius: "3px",
	padding: "8px",
	textAlign: "center",
	cursor: "pointer",
	transition: "border-color 0.2s ease",
	backgroundColor: "#F4F5F7",
	"&:hover": {
		borderColor: "#0052CC",
		backgroundColor: "#DEEBFF",
	},
	"&.dragover": {
		borderColor: "#0052CC",
		backgroundColor: "#DEEBFF",
	},
}));

const JiraTicketModal = ({ open, onClose, currentUser }) => {
	const [formData, setFormData] = useState({
		issueType: "",
		epicLink: "",
		summary: "",
		priority: "",
		description: "",
		labels: [],
		attachments: [],
	});

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [dragOver, setDragOver] = useState(false);

	// Helper function to get priority icon and color (matching JIRA board)
	const getPriorityConfig = (priority) => {
		switch (priority) {
			case "Priority 1":
				return {
					icon: (
						<KeyboardDoubleArrowUpIcon
							sx={{ fontSize: "16px", color: "#CD1316" }}
						/>
					),
					color: "#CD1316",
					bgColor: "#FFECEB",
				};
			case "Priority 2":
				return {
					icon: (
						<KeyboardArrowUpIcon sx={{ fontSize: "16px", color: "#EA7D24" }} />
					),
					color: "#2D8738",
					bgColor: "#F0F8F0",
				};
			case "Priority 3":
				return {
					icon: <RemoveIcon sx={{ fontSize: "16px", color: "#2D8738" }} />,
					color: "#EA7D24",
					bgColor: "#FFF4E6",
				};
			default:
				return {
					icon: (
						<KeyboardArrowUpIcon sx={{ fontSize: "16px", color: "#626F86" }} />
					),
					color: "#626F86",
					bgColor: "#F4F5F7",
				};
		}
	};
	const resetForm = () => {
		setFormData({
			issueType: "",
			epicLink: "",
			summary: "",
			priority: "",
			description: "",
			labels: [],
			attachments: [],
		});
		setError("");
		setSuccess("");
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleInputChange = (field) => (event) => {
		setFormData((prev) => ({
			...prev,
			[field]: event.target.value,
		}));
	};
	const handleFileUpload = (files) => {
		if (files && files.length > 0) {
			const validFiles = [];
			let hasError = false;

			for (let i = 0; i < files.length; i++) {
				const file = files[i];

				// Check file size (max 10MB per file)
				if (file.size > 10 * 1024 * 1024) {
					setError(
						`File "${file.name}" is too large. Maximum file size is 10MB.`,
					);
					hasError = true;
					break;
				}

				// Check if file already exists
				const existingFile = formData.attachments.find(
					(existingFile) =>
						existingFile.name === file.name && existingFile.size === file.size,
				);

				if (!existingFile) {
					validFiles.push(file);
				}
			}

			if (!hasError && validFiles.length > 0) {
				setFormData((prev) => ({
					...prev,
					attachments: [...prev.attachments, ...validFiles],
				}));
				setError(""); // Clear any previous errors
			}
		}
	};
	const handleFileSelect = (event) => {
		const files = Array.from(event.target.files);
		handleFileUpload(files);
		// Reset the input value so the same file can be selected again if needed
		event.target.value = "";
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = (event) => {
		event.preventDefault();
		setDragOver(false);
	};
	const handleDrop = (event) => {
		event.preventDefault();
		setDragOver(false);
		const files = Array.from(event.dataTransfer.files);
		handleFileUpload(files);
	};
	const removeAttachment = (index) => {
		setFormData((prev) => ({
			...prev,
			attachments: prev.attachments.filter((_, i) => i !== index),
		}));
	};
	const createJiraTicket = async () => {
		try {
			setLoading(true);
			setError("");
			// Validate required fields
			if (
				!formData.issueType ||
				!formData.summary ||
				!formData.priority ||
				!formData.epicLink
			) {
				setError("Please fill in all required fields");
				return;
			}
			const payload = {
				fields: {
					project: {
						key: "MPM",
					},
					summary: formData.summary,
					description: formData.description || "No description provided",
					issuetype: {
						name: formData.issueType,
					},
					priority: {
						name: formData.priority,
					},
					// Add labels if provided
					labels: formData.labels.length > 0 ? formData.labels : [],
					// Custom fields (if needed)
					customfield_19847: "P12345", // P number (project number) - could be dynamic
					customfield_19811: "K67890", // K number (Cost number) - could be dynamic
					customfield_19602: "WP001", // WP number - could be dynamic
				},
				epicLink: formData.epicLink, // Pass epic link separately for backend processing
			}; // Add epic link to custom field if provided
			if (formData.epicLink) {
				payload.fields.customfield_10206 = formData.epicLink;
			} // Prepare attachment data if files are selected
			let attachmentData = null;
			if (formData.attachments && formData.attachments.length > 0) {
				try {
					// Convert all files to base64
					const attachmentPromises = formData.attachments.map((file) => {
						return new Promise((resolve, reject) => {
							const reader = new FileReader();
							reader.onload = () => {
								// Remove the data URL prefix (e.g., "data:image/png;base64,")
								const base64 = reader.result.split(",")[1];
								resolve({
									name: file.name,
									size: file.size,
									type: file.type,
									content: base64,
								});
							};
							reader.onerror = reject;
							reader.readAsDataURL(file);
						});
					});

					attachmentData = await Promise.all(attachmentPromises);
				} catch (error) {
					console.error("Error converting files to base64:", error);
					setError("Failed to process attachments. Please try again.");
					return;
				}
			} // Use the authenticated apiClient instead of fetch
			console.log("🌐 API Configuration Debug:");
			console.log("  - Hostname:", window.location.hostname);
			console.log("  - Environment:", getEnvironment());
			console.log("  - Endpoint: /api/jira/create-ticket");
			console.log("  - Payload:", {
				fields: payload.fields,
				user: currentUser,
			});
			const response = await apiClient.post("/api/jira/create-ticket", {
				fields: payload.fields,
				user: currentUser,
				attachments: attachmentData,
			}); // Success response - apiClient automatically returns response.data
			const result = response.data;

			// Enhanced success message with ticket key prominently displayed
			const ticketKey = result.key;
			setSuccess(
				`✅ Ticket created successfully: ${ticketKey}${
					result.attachments?.uploaded
						? ` (with ${result.attachments.count} attachment${result.attachments.count > 1 ? "s" : ""})`
						: ""
				}`,
			);
			console.log("✅ Ticket created successfully:", result);

			// Show success message for longer time
			setTimeout(() => {
				handleClose();
			}, 3000);
		} catch (error) {
			console.error("Error creating JIRA ticket:", error);

			// Handle axios error response
			if (error.response) {
				// Server responded with error status
				const errorData = error.response.data;
				const errorMessage =
					errorData.detail ||
					errorData.message ||
					`Failed to create ticket (Status: ${error.response.status})`;
				console.error(
					"❌ Error response from API:",
					error.response.status,
					errorData,
				);
				setError(errorMessage);
			} else if (error.request) {
				// Network error
				console.error("❌ Network error:", error.request);
				setError("Network error. Please check your connection and try again.");
			} else {
				// Other error
				console.error("❌ Error:", error.message);
				setError("Failed to create ticket. Please try again.");
			}
		} finally {
			setLoading(false);
		}
	};
	return (
		<StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			{" "}
			<DialogTitle
				sx={{
					color: "black",
					fontWeight: 600,
					fontSize: "18px",
					padding: "16px 20px 12px 20px",
					borderBottom: "2px solid #DFE1E6",
					margin: 0,
				}}
			>
				🎫 Create JIRA Ticket
			</DialogTitle>
			<DialogContent
				sx={{
					padding: "20px",
					backgroundColor: "#FFFFFF",
					"& .MuiFormControl-root": {
						marginBottom: "12px",
					},
					"& .MuiTextField-root": {
						marginBottom: "12px",
					},
				}}
			>
				{error && (
					<Alert
						severity="error"
						sx={{
							mb: 2,
							borderRadius: "3px",
							backgroundColor: "#FFEBE6",
							border: "2px solid #DE350B",
							"& .MuiAlert-icon": {
								color: "#DE350B",
							},
						}}
					>
						{error}
					</Alert>
				)}
				{success && (
					<Alert
						severity="success"
						sx={{
							mb: 2,
							borderRadius: "3px",
							backgroundColor: "#E3FCEF",
							border: "2px solid #00875A",
							"& .MuiAlert-icon": {
								color: "#00875A",
							},
						}}
					>
						{success}
					</Alert>
				)}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0, mt: 2 }}>
					{" "}
					{/* Issue Type */}{" "}
					<FormControl
						fullWidth
						required
						sx={{
							mb: 1.5,
							mt: 1,
							"& .MuiInputLabel-root": {
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								transform: "translate(0, -14px) scale(1)",
								position: "static",
								mb: "-8px",
							},
							"& .MuiOutlinedInput-root": {
								backgroundColor: "#FAFBFC",
								borderRadius: "3px",
								fontSize: "14px",
								"& fieldset": {
									borderColor: "#DFE1E6",
								},
								"&:hover fieldset": {
									borderColor: "#B3BAC5",
								},
								"&.Mui-focused fieldset": {
									borderColor: "#0052CC",
									borderWidth: "2px",
								},
							},
						}}
					>
						<InputLabel shrink>Issue Type</InputLabel>
						<Select
							value={formData.issueType}
							onChange={handleInputChange("issueType")}
							displayEmpty
							renderValue={(selected) => {
								if (!selected)
									return (
										<span style={{ color: "#A5ADBA" }}>
											Select an issue type
										</span>
									);
								return (
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										{selected === "Task" ? (
											<>
												<AssignmentIcon
													sx={{ fontSize: "16px", color: "#0052CC" }}
												/>
												<span>Task</span>
											</>
										) : (
											<>
												<BugReportIcon
													sx={{ fontSize: "16px", color: "#DE350B" }}
												/>
												<span>Bug</span>
											</>
										)}
									</Box>
								);
							}}
						>
							{" "}
							<MenuItem
								value="Task"
								sx={{
									fontSize: "14px",
									"&:hover": { backgroundColor: "#DEEBFF" },
									py: 1.5,
								}}
							>
								<ListItemIcon sx={{ minWidth: "30px" }}>
									<AssignmentIcon sx={{ fontSize: "16px", color: "#0052CC" }} />
								</ListItemIcon>
								<ListItemText
									primary="Task"
									sx={{
										"& .MuiListItemText-primary": {
											fontSize: "14px",
											fontWeight: 400,
										},
									}}
								/>
							</MenuItem>
							<MenuItem
								value="Bug"
								sx={{
									fontSize: "14px",
									"&:hover": { backgroundColor: "#FFEBE6" },
									py: 1.5,
								}}
							>
								<ListItemIcon sx={{ minWidth: "30px" }}>
									<BugReportIcon sx={{ fontSize: "16px", color: "#DE350B" }} />
								</ListItemIcon>
								<ListItemText
									primary="Bug"
									sx={{
										"& .MuiListItemText-primary": {
											fontSize: "14px",
											fontWeight: 400,
										},
									}}
								/>
							</MenuItem>
						</Select>
					</FormControl>{" "}
					{/* Epic Link */}{" "}
					<FormControl
						fullWidth
						required
						sx={{
							mt: 1.5,
							"& .MuiInputLabel-root": {
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								transform: "translate(0, -14px) scale(1)",
								position: "static",
								mb: "-7px",
							},
							"& .MuiOutlinedInput-root": {
								backgroundColor: "#FAFBFC",
								borderRadius: "3px",
								fontSize: "14px",
								"& fieldset": {
									borderColor: "#DFE1E6",
								},
								"&:hover fieldset": {
									borderColor: "#B3BAC5",
								},
								"&.Mui-focused fieldset": {
									borderColor: "#0052CC",
									borderWidth: "2px",
								},
							},
						}}
					>
						<InputLabel shrink>Epic Link</InputLabel>
						<Select
							value={formData.epicLink}
							onChange={handleInputChange("epicLink")}
							displayEmpty
							renderValue={(selected) => {
								if (!selected)
									return (
										<span style={{ color: "#A5ADBA" }}>Select an epic</span>
									);
								return selected;
							}}
						>
							{" "}
							<MenuItem value="MPM-92" sx={{ fontSize: "14px", py: 1.5 }}>
								MPM-92: Parts tracking tool - Backend (Data Quality)
							</MenuItem>
							<MenuItem value="MPM-51" sx={{ fontSize: "14px", py: 1.5 }}>
								MPM-51: Parts tracking tool - Frontend
							</MenuItem>
						</Select>
					</FormControl>{" "}
					{/* Summary */}{" "}
					<Box sx={{ mb: 1.5 }}>
						<InputLabel
							shrink
							sx={{
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								position: "static",
								transform: "none",
								mb: "5px",
							}}
						>
							Summary *
						</InputLabel>
						<TextField
							fullWidth
							required
							value={formData.summary}
							onChange={handleInputChange("summary")}
							placeholder="What needs to be done?"
							sx={{
								"& .MuiOutlinedInput-root": {
									backgroundColor: "#FAFBFC",
									borderRadius: "3px",
									fontSize: "14px",
									"& fieldset": {
										borderColor: "#DFE1E6",
									},
									"&:hover fieldset": {
										borderColor: "#B3BAC5",
									},
									"&.Mui-focused fieldset": {
										borderColor: "#0052CC",
										borderWidth: "2px",
									},
								},
								"& .MuiInputBase-input": {
									padding: "6px 10px",
								},
							}}
						/>
					</Box>{" "}
					{/* Priority */}{" "}
					<FormControl
						fullWidth
						required
						sx={{
							mb: 1.5,
							"& .MuiInputLabel-root": {
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								transform: "translate(0, -14px) scale(1)",
								position: "static",
								mb: "-8px",
							},
							"& .MuiOutlinedInput-root": {
								backgroundColor: "#FAFBFC",
								borderRadius: "3px",
								fontSize: "14px",
								"& fieldset": {
									borderColor: "#DFE1E6",
								},
								"&:hover fieldset": {
									borderColor: "#B3BAC5",
								},
								"&.Mui-focused fieldset": {
									borderColor: "#0052CC",
									borderWidth: "2px",
								},
							},
						}}
					>
						<InputLabel shrink>Priority</InputLabel>
						<Select
							value={formData.priority}
							onChange={handleInputChange("priority")}
							displayEmpty
							renderValue={(selected) => {
								if (!selected)
									return (
										<span style={{ color: "#A5ADBA" }}>Select priority</span>
									);
								const config = getPriorityConfig(selected);
								return (
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										{config.icon}
										<span>{selected}</span>
									</Box>
								);
							}}
						>
							{" "}
							<MenuItem
								value="Priority 1"
								sx={{
									fontSize: "14px",
									"&:hover": { backgroundColor: "#FFEBE6" },
									py: 1.5,
								}}
							>
								<ListItemIcon sx={{ minWidth: "30px" }}>
									<KeyboardDoubleArrowUpIcon
										sx={{ fontSize: "16px", color: "#CD1316" }}
									/>
								</ListItemIcon>
								<ListItemText
									primary="Priority 1"
									sx={{
										"& .MuiListItemText-primary": {
											fontSize: "14px",
											fontWeight: 400,
										},
									}}
								/>
							</MenuItem>
							<MenuItem
								value="Priority 2"
								sx={{
									fontSize: "14px",
									"&:hover": { backgroundColor: "#FFF4E6" },
									py: 1.5,
								}}
							>
								<ListItemIcon sx={{ minWidth: "30px" }}>
									<KeyboardArrowUpIcon
										sx={{ fontSize: "16px", color: "#EA7D24" }}
									/>
								</ListItemIcon>
								<ListItemText
									primary="Priority 2"
									sx={{
										"& .MuiListItemText-primary": {
											fontSize: "14px",
											fontWeight: 400,
										},
									}}
								/>
							</MenuItem>
							<MenuItem
								value="Priority 3"
								sx={{
									fontSize: "14px",
									"&:hover": { backgroundColor: "#E3FCEF" },
									py: 1.5,
								}}
							>
								<ListItemIcon sx={{ minWidth: "30px" }}>
									<RemoveIcon sx={{ fontSize: "16px", color: "#2D8738" }} />
								</ListItemIcon>
								<ListItemText
									primary="Priority 3"
									sx={{
										"& .MuiListItemText-primary": {
											fontSize: "14px",
											fontWeight: 400,
										},
									}}
								/>
							</MenuItem>
						</Select>
					</FormControl>{" "}
					{/* Description */}{" "}
					<Box sx={{ mb: 1.5 }}>
						<InputLabel
							shrink
							sx={{
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								marginBottom: "4px",
								position: "static",
								transform: "none",
							}}
						>
							Description
						</InputLabel>
						<TextField
							fullWidth
							multiline
							rows={2}
							value={formData.description}
							onChange={handleInputChange("description")}
							placeholder="Describe the issue in as much detail as you'd like."
							sx={{
								"& .MuiOutlinedInput-root": {
									backgroundColor: "#FAFBFC",
									borderRadius: "3px",
									fontSize: "14px",
									"& fieldset": {
										borderColor: "#DFE1E6",
									},
									"&:hover fieldset": {
										borderColor: "#B3BAC5",
									},
									"&.Mui-focused fieldset": {
										borderColor: "#0052CC",
										borderWidth: "2px",
									},
								},
								"& .MuiInputBase-input": {
									padding: "6px 10px",
								},
							}}
						/>
					</Box>
					{/* Labels */}{" "}
					<Box sx={{ mb: 1.5 }}>
						<InputLabel
							shrink
							sx={{
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								marginBottom: "4px",
								position: "static",
								transform: "none",
							}}
						>
							Labels
						</InputLabel>
						<Autocomplete
							multiple
							freeSolo
							value={formData.labels}
							onChange={(event, newValue) => {
								setFormData((prev) => ({
									...prev,
									labels: newValue,
								}));
							}}
							options={[
								"parts_tracking_frontend",
								"parts_tracking_backend",
								"bug",
								"enhancement",
								"urgent",
								"data-quality",
								"ui-ux",
								"performance",
								"security",
								"documentation",
							]}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										variant="outlined"
										label={option}
										size="small"
										{...getTagProps({ index })}
										sx={{
											backgroundColor: "#DFE1E6",
											borderColor: "#C1C7D0",
											color: "#172B4D",
											fontSize: "11px",
											height: "20px",
											borderRadius: "3px",
											"& .MuiChip-deleteIcon": {
												color: "#5E6C84",
												fontSize: "14px",
											},
										}}
									/>
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									placeholder="Type to search for labels"
									variant="outlined"
									sx={{
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#FAFBFC",
											borderRadius: "3px",
											fontSize: "14px",
											"& fieldset": {
												borderColor: "#DFE1E6",
											},
											"&:hover fieldset": {
												borderColor: "#B3BAC5",
											},
											"&.Mui-focused fieldset": {
												borderColor: "#0052CC",
												borderWidth: "2px",
											},
										},
										"& .MuiInputBase-input": {
											padding: "6px 10px !important",
										},
									}}
								/>
							)}
							sx={{
								"& .MuiAutocomplete-tag": {
									margin: "2px",
								},
							}}
						/>
					</Box>{" "}
					{/* File Attachments */}{" "}
					<Box sx={{ mb: 1.5 }}>
						<InputLabel
							shrink
							sx={{
								color: "#5E6C84",
								fontSize: "12px",
								fontWeight: 600,
								marginBottom: "4px",
								position: "static",
								transform: "none",
							}}
						>
							Attachments (Optional)
						</InputLabel>
						{/* File Upload Area */}{" "}
						<FileUploadBox
							className={dragOver ? "dragover" : ""}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={() => document.getElementById("file-upload").click()}
						>
							<AttachFileIcon sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
							<Typography variant="body2" sx={{ color: "#666" }}>
								Click to select files or drag and drop
							</Typography>
							<Typography variant="caption" sx={{ color: "#999" }}>
								Maximum file size: 10MB per file • Multiple files supported
							</Typography>
							<Input
								id="file-upload"
								type="file"
								multiple
								onChange={handleFileSelect}
								sx={{ display: "none" }}
							/>
						</FileUploadBox>
						{/* Display Selected Files */}
						{formData.attachments && formData.attachments.length > 0 && (
							<Box sx={{ mt: 1 }}>
								{formData.attachments.map((file, index) => (
									<Box
										key={index}
										sx={{
											border: "1px solid #DFE1E6",
											borderRadius: "3px",
											padding: "6px 8px",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											mb: 0.5,
											backgroundColor: "#F4F5F7",
											fontSize: "12px",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<AttachFileIcon
												sx={{ color: "#5E6C84", fontSize: "14px" }}
											/>
											<Box>
												<Typography
													variant="body2"
													sx={{ fontSize: "12px", fontWeight: 500 }}
												>
													{file.name}
												</Typography>
												<Typography
													variant="caption"
													sx={{ color: "#6B778C", fontSize: "11px" }}
												>
													{(file.size / 1024).toFixed(1)} KB
												</Typography>
											</Box>
										</Box>
										<Button
											onClick={() => removeAttachment(index)}
											size="small"
											sx={{
												color: "#5E6C84",
												minWidth: "auto",
												padding: "2px",
												"&:hover": {
													backgroundColor: "#DFE1E6",
													color: "#172B4D",
												},
											}}
										>
											<DeleteOutlineIcon sx={{ fontSize: "14px" }} />
										</Button>
									</Box>
								))}
							</Box>
						)}
					</Box>
				</Box>{" "}
			</DialogContent>{" "}
			<DialogActions
				sx={{
					padding: "12px 20px",
					gap: 1,
					justifyContent: "flex-end",
					borderTop: "2px solid #DFE1E6",
					backgroundColor: "#FAFBFC",
				}}
			>
				{" "}
				<Button
					onClick={createJiraTicket}
					variant="contained"
					disabled={
						loading ||
						!formData.issueType ||
						!formData.summary ||
						!formData.priority ||
						!formData.epicLink
					}
					sx={{
						textTransform: "none",
						backgroundColor: "#0052CC",
						fontSize: "14px",
						minWidth: "80px",
						height: "32px",
						fontWeight: 500,
						borderRadius: "3px",
						boxShadow: "none",
						"&:hover": {
							backgroundColor: "#0747A6",
							boxShadow: "none",
						},
						"&:disabled": {
							backgroundColor: "#F4F5F7",
							color: "#A5ADBA",
						},
					}}
				>
					{loading ? (
						<>
							<CircularProgress size={14} sx={{ mr: 1, color: "white" }} />
							Creating...
						</>
					) : (
						"Create"
					)}
				</Button>
				<Button
					onClick={handleClose}
					variant="text"
					sx={{
						textTransform: "none",
						color: "#5E6C84",
						fontSize: "14px",
						minWidth: "80px",
						height: "32px",
						fontWeight: 500,
						"&:hover": {
							backgroundColor: "#F4F5F7",
						},
					}}
				>
					Cancel
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

export default JiraTicketModal;
